import { Check, Loading01 } from "@untitledui/icons";
import type { ReactElement } from "react";
import {
	DisplayFieldFeedback,
	type DisplayFieldFeedback as DisplayFieldFeedbackType,
	FEEDBACK_SAVED_FADE_MS,
} from "@/components/Atoms/DisplayField/display-field.shared";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";

export interface FeedbackIndicatorProps {
	/** Auto-save feedback state. Idle / Empty renders nothing. */
	feedback?: DisplayFieldFeedbackType;
	className?: string;
	"data-testid"?: string;
}

/**
 * Compact auto-save status chip for Input / Select / TextArea label rows.
 *
 * @see Figma FeedbackIndicator 4895:57620 — Saving / Saved / End
 */
export function FeedbackIndicator({
	feedback = DisplayFieldFeedback.Idle,
	className,
	"data-testid": dataTestId = "feedback-indicator",
}: FeedbackIndicatorProps): ReactElement | null {
	const { t } = useAppTranslation();

	if (feedback === DisplayFieldFeedback.Idle || feedback === undefined) {
		return null;
	}

	const savingLabel = t("common.actions.saving");
	const savedLabel = t("common.actions.saved");

	if (feedback === DisplayFieldFeedback.Saving) {
		return (
			<span
				className={cx(
					"flex shrink-0 items-center gap-1 text-xs font-semibold text-quaternary",
					className,
				)}
				aria-live="polite"
				data-testid={dataTestId}
				data-state="saving"
			>
				<Loading01 className="size-3.5 animate-spin text-fg-quaternary" />
				{savingLabel}
			</span>
		);
	}

	if (
		feedback === DisplayFieldFeedback.Saved ||
		feedback === DisplayFieldFeedback.End
	) {
		return (
			<span
				className={cx(
					"flex shrink-0 items-center gap-1 text-xs font-semibold text-success-primary transition-opacity",
					feedback === DisplayFieldFeedback.End && "opacity-0",
					className,
				)}
				style={{ transitionDuration: `${FEEDBACK_SAVED_FADE_MS}ms` }}
				aria-live="polite"
				data-testid={dataTestId}
				data-state={feedback === DisplayFieldFeedback.End ? "end" : "saved"}
			>
				<Check className="size-3.5 text-success-primary" />
				{savedLabel}
			</span>
		);
	}

	return null;
}
